/**
 * @ngdoc service
 * @name ims.shared:ColorRenderer
 * @description
 * Instantiable Color Renderer. Returns an object with methods of accessing color.
 * @params
 * points
 * type
 * reverse
 * default
 */
import { Cesium } from 'app/cesium';
import { createCesiumColorForStrOrArrayColor } from 'app/shared/utilities/ims_color';
import * as _ from 'lodash';
import { ColorConfig, ColorConfigType } from '../collections/StatDefinition';

export abstract class BaseColorRenderer {
    default: Cesium.Color;

    public abstract getColor(number): Cesium.Color;

    public getColorAsCssRgba(value): string {
        const cesiumColor = this.getColor(value);
        const r = cesiumColor.red * 255;
        const g = cesiumColor.green * 255;
        const b = cesiumColor.blue * 255;
        const a = cesiumColor.alpha;
        return 'rgba(' + Math.floor(r) + ',' + Math.floor(g) + ',' + Math.floor(b) + ',' + a + ')';
    }
}

export default class ColorRenderer<PointValueType = string | number> extends BaseColorRenderer {
    public points: Array<{
        value: PointValueType;
        color: Cesium.Color;
        display?: boolean;
    }>;
    public readonly default: Cesium.Color;
    public readonly type: ColorConfigType;
    public readonly reverse: boolean;
    public readonly defaultName: string;

    /**
     *
     * @param params is a dictionary with the following spec
     *
     *       points: [
     *          {
     *              value: number,
     *              color: CesiumColor or IMSColor or RGBA array
     *          }
     *      ],
     *      // default if the value is null
     *      default: CesiumColor or IMSColor,
     *      type: 'traffic_light' or 'gradient'
     * }
     */
    constructor(params: ColorConfig<PointValueType>) {
        super();
        if (!params) {
            throw new Error(`params must be truthy`);
        }
        const sortedPoints = _.sortBy(params.points, 'value').reverse();
        // Upon submission parameters are converted to cesium colors
        this.points = sortedPoints.map((entry) => {
            return {
                value: entry.value,
                color: createCesiumColorForStrOrArrayColor(entry.color),
                display: entry.display,
            };
        });
        this.default =
            params && params.default
                ? createCesiumColorForStrOrArrayColor(params.default)
                : Cesium.Color.WHITE;

        this.type = params.type;
        this.reverse = params.reverse;
        this.defaultName = params.default_name;
    }

    /**
     * Gets a cesium color
     * @param value
     * @returns {*}
     */
    getColor(value: PointValueType | null | undefined): Cesium.Color {
        //If our given value is greater than our max value, we return our max values color irrelevant of gradient or traffic light
        if (value === null || _.isNaN(value)) {
            return this.default;
        }
        if (typeof value === 'undefined') {
            return Cesium.Color.WHITE;
        }

        // If not exact and value is outside of the specified points, return relevant max or min point color
        if (this.type !== 'exact') {
            // if we don't have any points - then our only option is the default color;
            if (!this.points.length) {
                return this.default;
            }

            //We check if it's greater than our largest value as it wont need interpolating.
            // TODO: not sure if we should just assume the top one is the max point? Same with the min point?
            const maxPoint = this.points[0];
            if (value > maxPoint.value) {
                return maxPoint.color;
            }

            //We also do the same for the less than the lowest value.
            const minPoint = this.points[this.points.length - 1];
            if (value <= minPoint.value) {
                return minPoint.color;
            }
        }

        switch (this.type) {
            case 'traffic_light':
                return ColorRenderer._trafficLight(value, this.points, this.reverse, this.default);
            case 'gradient':
                return ColorRenderer._gradient(value, this.points, this.default);
            case 'exact':
                return ColorRenderer._exact(value, this.points, this.default);
            default:
                return this.default;
        }
    }

    /**
     * Returns the most contrasting colour out of black or white as a css rgb string when provided with a value
     * @param value The value to calculate the contrasting color from
     * @return {string} A CSS rgb() string
     */
    getContrastingColorAsCssRgba(value): string {
        const cesiumColor = this.getColor(value);

        const r = cesiumColor.red * 255;
        const g = cesiumColor.green * 255;
        const b = cesiumColor.blue * 255;

        let cssRgba = 'rgb(0,0,0)';
        const brightness = ColorRenderer.getColorBrightness(r, g, b);

        if (brightness <= 123) {
            cssRgba = 'rgb(255,255,255)';
        }

        return cssRgba;
    }

    /**
     * Given 8-bit (0 to 255) red, green and blue values, the above formula returns a value from 0 to 255 that
     * indicates brightness, with zero being the darkest and 255 the brightest value.
     * @param r {number} (0-255) red value
     * @param g {number} (0-255) green value
     * @param b {number} (0-255) blue value
     * @return {number} (0-255) brightness value
     */
    static getColorBrightness(r: number, g: number, b: number): number {
        return (r * 299 + g * 587 + b * 114) / 1000;
    }

    /**
     * Interpolates the response colour between the point that defines the color and the next
     * @param value The value to get a color for
     * @param points The points describing the color to value mappings
     * @param defaultColor The default color of the color renderer to use if no match found (failover)
     * @private
     */
    static _gradient(value, points, defaultColor: Cesium.Color): Cesium.Color {
        for (let i = 0; i < points.length - 1; i++) {
            if (value <= points[i].value && value > points[i + 1].value) {
                const topPoint = points[i];
                const bottomPoint = points[i + 1];
                //work out total difference
                const totalDiff = topPoint.value - bottomPoint.value;
                const ourDiff = topPoint.value - value;
                //work out percentage difference
                const ratio = ourDiff / totalDiff;
                //apply percentage difference to RGB values
                let red = (topPoint.color.red - bottomPoint.color.red) * ratio;
                let blue = (topPoint.color.blue - bottomPoint.color.blue) * ratio;
                let green = (topPoint.color.green - bottomPoint.color.green) * ratio;
                //return result cesium color
                red = topPoint.color.red - red;
                blue = topPoint.color.blue - blue;
                green = topPoint.color.green - green;
                return new Cesium.Color(red, green, blue);
            }
        }
        return defaultColor;
    }

    /**
     * Matches a color to any values between the point that defines color and the next (or previous if reverse=true)
     * @param value The value to get a color for
     * @param points The points describing the color to value mappings
     * @param reverse Whether or not to reverse the color mapping order
     * @param defaultColor The default color of the color renderer to use if no match found (failover)
     * @private
     */
    static _trafficLight(value, points, reverse, defaultColor: Cesium.Color): Cesium.Color {
        //If reverse is true, then we look ahead for the value we use instead of where we are.
        if (!reverse) {
            reverse = 0;
        } else {
            reverse = 1;
        }
        //We don't check the last value as it gets compared as we go through top to bottom.
        for (let i = 0; i < points.length - 1; i++) {
            if (value <= points[i].value && value > points[i + 1].value) {
                //By adding reverse, we get the value it's going to, not the value it's come from (effectively, reversing the condition.)
                return points[i + reverse].color;
            }
        }

        return defaultColor;
    }

    /**
     * Matches exact values to colours. If there isn't an exact color to value match, the default is used.
     * @param value The value to get a color for
     * @param points The points describing the color to value mappings
     * @param defaultColor The default color to use if an exact match isn't found
     * @private
     */
    static _exact(value, points, defaultColor: Cesium.Color): Cesium.Color {
        for (let i = 0; i < points.length; i++) {
            if (value == points[i].value) {
                return points[i].color;
            }
        }
        return defaultColor;
    }
}
